import { CellContext, ColumnDef, Row, Table } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { LuPause, LuPlay } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import {
    Account,
    AccountGroupProfile,
    AccountType,
    LifecycleStatus,
    ManagementType,
    PortfolioEngineAccount,
    TradingStatus,
} from '@ndvr/portfolio-api';

import { FilterType } from '@/cip/_openapi/portfolio-optimizer';
import { BulkSelectionBar } from '@/components/BulkSelectionBar';
import { ClientDataTable } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { accountRoute, formatAccountType, formatCurrency, formatCurrencyHuman, titleCase } from 'utils';
import { parseNumberStr } from 'utils/number-utils';
import { getEffectiveTaxBudget, getTaxBudgetUsage } from 'views/account/tax-budget';
import { AccountRowMenu } from 'views/household/OverviewTab/components/AccountRowMenu';
import {
    TradeStatusAction,
    TradeStatusConfirmDialog,
} from 'views/portfolio-engine/all-accounts/TradeStatusConfirmDialog';
import { useBulkSetAccountsTradeStatus, useDeleteAccount } from 'views/portfolio/account-queries';
import { isAccountPolicyCoordinated } from 'views/portfolio/policy-coordination';
import { DeleteValidationModal } from 'widgets/delete/DeleteValidationModal';

type AccountGrouping = 'none' | 'accountGroup' | 'accountType' | 'boostStatus';
type HouseholdAccountRow = PortfolioEngineAccount & { taxLiability?: number };

interface AccountStatusPresentation {
    label: string;
    badgeClassName: string;
    dotClassName: string;
}

const tradingStatusPresentations = {
    [TradingStatus.AUTOMATIC]: {
        label: 'Active',
        badgeClassName: 'bg-green-50 text-green-800',
        dotClassName: 'bg-green-600',
    },
    [TradingStatus.EXTERNAL]: {
        label: 'Not Traded',
        badgeClassName: 'bg-stone-100 text-stone-600',
        dotClassName: 'bg-stone-400',
    },
    [TradingStatus.MANUAL]: {
        label: 'Manual',
        badgeClassName: 'bg-stone-100 text-stone-600',
        dotClassName: 'bg-stone-400',
    },
    [TradingStatus.SUSPENDED]: {
        label: 'Suspended',
        badgeClassName: 'bg-red-0 text-red-500',
        dotClassName: 'bg-red-500',
    },
    [TradingStatus.REBALANCE_IN_PROGRESS]: {
        label: 'Rebalance in Progress',
        badgeClassName: 'bg-blue-50 text-blue-700',
        dotClassName: 'bg-blue-500',
    },
} satisfies Record<TradingStatus, AccountStatusPresentation>;

interface HouseholdAccountTableProps {
    householdGUID: string;
    accounts: PortfolioEngineAccount[];
    accountGroupProfiles: ReadonlyMap<string, AccountGroupProfile>;
    taxLiabilityByAccountGUID: ReadonlyMap<string, number | undefined>;
    isTaxLiabilityLoading: boolean;
    isLoading: boolean;
}

const canChangeTradingStatus = (account: PortfolioEngineAccount) =>
    account.lifecycleStatus === LifecycleStatus.REAL &&
    account.tradingStatus !== TradingStatus.MANUAL &&
    account.tradingStatus !== TradingStatus.EXTERNAL;

const getTradingStatusDisabledReason = (account: PortfolioEngineAccount) => {
    if (account.lifecycleStatus === LifecycleStatus.CLOSED) {
        return 'Closed accounts cannot have their trading status changed.';
    }
    if (account.externallyManaged) {
        return 'Held-away accounts cannot have their trading status changed.';
    }
    if (account.lifecycleStatus !== LifecycleStatus.REAL) {
        return 'Link this account before changing its trading status.';
    }
    if (account.tradingStatus === TradingStatus.EXTERNAL) {
        return 'Externally traded accounts cannot have their trading status changed.';
    }
    if (account.tradingStatus === TradingStatus.MANUAL) {
        return 'Onboard this account to Portfolio Boost before changing its trading status.';
    }
    return undefined;
};

const SelectionCheckbox = ({
    checked,
    onCheckedChange,
    label,
    disabledReason,
}: {
    checked: boolean | 'indeterminate';
    onCheckedChange: (checked: boolean | 'indeterminate') => void;
    label: string;
    disabledReason?: string;
}) => {
    if (!disabledReason) {
        return <Checkbox checked={checked} onCheckedChange={onCheckedChange} aria-label={label} />;
    }

    return (
        <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
                <span
                    role="checkbox"
                    aria-checked={checked === 'indeterminate' ? 'mixed' : checked}
                    aria-disabled="true"
                    aria-label={`${label}. ${disabledReason}`}
                    tabIndex={0}
                    className="focus-visible:ring-ring inline-flex cursor-not-allowed rounded focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                    onClick={(event) => event.stopPropagation()}
                >
                    <Checkbox
                        checked={checked}
                        disabled
                        aria-hidden="true"
                        tabIndex={-1}
                        className="pointer-events-none"
                    />
                </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{disabledReason}</TooltipContent>
        </Tooltip>
    );
};

const ValueWithCash = ({ value, cash }: { value: number; cash: number }) => (
    <div>
        <div>{formatCurrency(value)}</div>
        <div className="text-cocoa-500 mt-0.5 text-xs font-medium">{formatCurrency(cash)} cash</div>
    </div>
);

const TaxLiabilityWithBudget = ({ account, isLoading }: { account: HouseholdAccountRow; isLoading: boolean }) => {
    if (isLoading || account.taxLiability === undefined) return '—';

    const budget = getEffectiveTaxBudget(account);
    const liability = formatCurrencyHuman(account.taxLiability, 'USD');

    if (budget.kind !== 'amount') return liability;

    const { overBudget, percent } = getTaxBudgetUsage(budget.amount, account.taxLiability);

    return (
        <div className="ml-auto w-32">
            <div className="whitespace-nowrap">
                {liability}{' '}
                <span className="text-cocoa-500 font-normal">/ {formatCurrencyHuman(budget.amount, 'USD')}</span>
            </div>
            <Progress
                value={percent}
                aria-label={`${percent}% of tax budget reached`}
                className={`bg-cocoa-100 mt-1 h-1.5 ${overBudget ? '[&>div]:bg-gold-600' : '[&>div]:bg-gold-300'}`}
            />
        </div>
    );
};

const getAccountGroupKey = (
    account: HouseholdAccountRow,
    groupBy: Exclude<AccountGrouping, 'none'>,
    accountGroupProfiles: ReadonlyMap<string, AccountGroupProfile>,
): string => {
    switch (groupBy) {
        case 'accountGroup': {
            const profile = accountGroupProfiles.get(account.accountGroupGUID);
            if (!profile || !isAccountPolicyCoordinated(account, profile)) return 'Ungrouped';
            return profile.accountGroup.accountGroupNamePlain ?? profile.accountGroup.accountGroupName.plainText;
        }
        case 'accountType':
            return formatAccountType(account.accountType);
        case 'boostStatus':
            return account.managementType === ManagementType.AUTOMATIC ? 'Boosted' : 'Not Boosted';
    }
};

const getTradingEligibleAccountIds = (rows: Row<HouseholdAccountRow>[]) =>
    rows.flatMap((row) => {
        const leafRows = row.subRows.length > 0 ? row.getLeafRows() : [row];
        return leafRows
            .filter((leafRow) => canChangeTradingStatus(leafRow.original))
            .map((leafRow) => leafRow.original.accountGUID);
    });

const getAccountStatusPresentation = (account: PortfolioEngineAccount): AccountStatusPresentation => {
    if (account.lifecycleStatus === LifecycleStatus.CLOSED) {
        return { label: 'Closed', badgeClassName: 'bg-stone-100 text-stone-600', dotClassName: 'bg-stone-400' };
    }
    if (account.lifecycleStatus !== LifecycleStatus.REAL) {
        return {
            label:
                account.lifecycleStatus === LifecycleStatus.DRAFT && account.externallyManaged
                    ? 'Held Away'
                    : 'Not Linked',
            badgeClassName: 'bg-stone-100 text-stone-600',
            dotClassName: 'bg-stone-400',
        };
    }

    return tradingStatusPresentations[account.tradingStatus];
};

export const HouseholdAccountTable = ({
    householdGUID,
    accounts,
    accountGroupProfiles,
    taxLiabilityByAccountGUID,
    isTaxLiabilityLoading,
    isLoading,
}: HouseholdAccountTableProps) => {
    const [groupBy, setGroupBy] = useState<AccountGrouping>('none');
    const [accountToDelete, setAccountToDelete] = useState<Account>();
    const tableAccounts = useMemo<HouseholdAccountRow[]>(
        () =>
            accounts.map((account) => ({
                ...account,
                taxLiability:
                    account.accountType === AccountType.TAXABLE
                        ? taxLiabilityByAccountGUID.get(account.accountGUID)
                        : undefined,
            })),
        [accounts, taxLiabilityByAccountGUID],
    );
    const [selectedAccountIds, setSelectedAccountIds] = useState<Set<string>>(new Set());
    const [tradeStatusConfirm, setTradeStatusConfirm] = useState<{
        accounts: PortfolioEngineAccount[];
        action: TradeStatusAction;
        totalSelectedCount: number;
    } | null>(null);
    const { mutate: setAccountsTradeStatus, isPending: isUpdatingTradeStatus } = useBulkSetAccountsTradeStatus();
    const { mutateAsync: deleteAccount, isPending: isDeletingAccount } = useDeleteAccount(householdGUID);
    const updateSelection = useCallback((accountIds: string[], selected: boolean) => {
        setSelectedAccountIds((current) => {
            const next = new Set(current);
            accountIds.forEach((accountId) => (selected ? next.add(accountId) : next.delete(accountId)));
            return next;
        });
    }, []);
    const renderSelectionCell = useCallback(
        ({ row }: CellContext<HouseholdAccountRow, unknown>) => {
            const isGroupRow = row.getIsGrouped();
            const accountIds = getTradingEligibleAccountIds([row]);
            const allSelected =
                accountIds.length > 0 && accountIds.every((accountId) => selectedAccountIds.has(accountId));
            const someSelected = accountIds.some((accountId) => selectedAccountIds.has(accountId));
            const accountName = row.original.accountNamePlain ?? row.original.accountName.plainText;
            const label = isGroupRow ? `Select ${String(row.groupingValue)}` : `Select ${accountName}`;
            const disabledReason =
                accountIds.length === 0
                    ? isGroupRow
                        ? 'No accounts in this group are eligible for trading status changes.'
                        : getTradingStatusDisabledReason(row.original)
                    : undefined;

            return (
                <span onClick={(event) => event.stopPropagation()}>
                    <SelectionCheckbox
                        checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                        onCheckedChange={(checked) => updateSelection(accountIds, checked === true)}
                        label={label}
                        disabledReason={disabledReason}
                    />
                </span>
            );
        },
        [selectedAccountIds, updateSelection],
    );
    const columns = useMemo<ColumnDef<HouseholdAccountRow>[]>(
        () => [
            {
                id: 'select',
                enableResizing: false,
                header: ({ table }: { table: Table<HouseholdAccountRow> }) => {
                    const visibleAccountIds = getTradingEligibleAccountIds(table.getRowModel().rows);
                    const allSelected =
                        visibleAccountIds.length > 0 &&
                        visibleAccountIds.every((accountId) => selectedAccountIds.has(accountId));
                    const someSelected = visibleAccountIds.some((accountId) => selectedAccountIds.has(accountId));

                    return (
                        <SelectionCheckbox
                            checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                            onCheckedChange={(checked) => updateSelection(visibleAccountIds, checked === true)}
                            label="Select all accounts"
                            disabledReason={
                                visibleAccountIds.length === 0
                                    ? 'No accounts are eligible for trading status changes.'
                                    : undefined
                            }
                        />
                    );
                },
                cell: renderSelectionCell,
                aggregatedCell: renderSelectionCell,
                enableSorting: false,
                meta: {
                    cellClassName: 'w-12 pl-6 pr-0 py-4',
                },
            },
            {
                id: 'account',
                accessorFn: (account) => account.accountNamePlain ?? account.accountName.plainText,
                header: 'Account',
                cell: ({ row }) => {
                    const account = row.original;
                    const accountDetails = [
                        account.custodyAccountId,
                        account.custodyType ? titleCase(account.custodyType) : undefined,
                        formatAccountType(account.accountType),
                    ].filter(Boolean);

                    return (
                        <Link to={accountRoute(account.accountGUID)} className="group block">
                            <div className="text-cocoa-900 group-hover:text-seafoam-700 text-sm font-semibold">
                                {account.accountNamePlain ?? account.accountName.plainText}
                            </div>
                            <div className="text-cocoa-500 mt-0.5 text-xs">{accountDetails.join(' · ')}</div>
                        </Link>
                    );
                },
                meta: {
                    filterType: FilterType.STRING,
                    headerClassName: 'px-6',
                    cellClassName: 'px-6 py-4',
                },
            },
            {
                id: 'tradingStatus',
                accessorFn: (account) => getAccountStatusPresentation(account).label,
                header: 'Status',
                cell: ({ row }) => {
                    const { label, badgeClassName, dotClassName } = getAccountStatusPresentation(row.original);

                    return (
                        <span
                            className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-xs font-semibold tracking-wide uppercase ${badgeClassName}`}
                        >
                            <span className={`h-2 w-2 rounded-full ${dotClassName}`} />
                            {label}
                        </span>
                    );
                },
                enableSorting: false,
                meta: {
                    filterType: FilterType.LIST,
                    filterOptionsFn: (table?: Table<HouseholdAccountRow>) => {
                        const statuses = new Set(
                            table
                                ?.getCoreRowModel()
                                .rows.map((row) => getAccountStatusPresentation(row.original).label),
                        );
                        return Array.from(statuses).sort();
                    },
                    cellClassName: 'py-4',
                },
            },
            {
                id: 'taxLiability',
                accessorFn: (account) => account.taxLiability,
                header: 'Tax Liability',
                cell: ({ row }) => <TaxLiabilityWithBudget account={row.original} isLoading={isTaxLiabilityLoading} />,
                aggregatedCell: ({ row }) => {
                    if (isTaxLiabilityLoading) return '—';
                    const taxLiabilities = row
                        .getLeafRows()
                        .map((leafRow) => leafRow.original.taxLiability)
                        .filter((value): value is number => value !== undefined);
                    return taxLiabilities.length > 0
                        ? formatCurrency(taxLiabilities.reduce((total, value) => total + value, 0))
                        : '—';
                },
                sortUndefined: 'last',
                meta: {
                    filterType: FilterType.NUMBER,
                    align: 'right',
                    headerClassName: 'px-6',
                    cellClassName: 'px-6 py-4 text-sm font-semibold tabular-nums',
                },
            },
            {
                id: 'aum',
                accessorFn: (account) => parseNumberStr(account.aum),
                header: 'Value',
                cell: ({ getValue, row }) => (
                    <ValueWithCash value={getValue<number>()} cash={parseNumberStr(row.original.cash)} />
                ),
                aggregatedCell: ({ getValue, row }) => (
                    <ValueWithCash
                        value={getValue<number>()}
                        cash={row
                            .getLeafRows()
                            .reduce((total, leafRow) => total + parseNumberStr(leafRow.original.cash), 0)}
                    />
                ),
                aggregationFn: 'sum',
                meta: {
                    filterType: FilterType.NUMBER,
                    align: 'right',
                    headerClassName: 'px-6',
                    cellClassName: 'px-6 py-4 text-sm font-semibold tabular-nums',
                },
            },
            {
                id: 'actions',
                enableResizing: false,
                cell: ({ row }) => {
                    const account = row.original;
                    const canChangeTradeStatus = canChangeTradingStatus(account);
                    const tradeStatusAction: TradeStatusAction = account.tradingSuspendedAt ? 'enable' : 'suspend';

                    return (
                        <div className="flex justify-end">
                            <AccountRowMenu
                                account={account}
                                householdGUID={householdGUID}
                                viewAccountUrl={accountRoute(account.accountGUID)}
                                showLinkAccountAction
                                canChangeTradeStatus={canChangeTradeStatus}
                                onDelete={setAccountToDelete}
                                tradingSuspended={!!account.tradingSuspendedAt}
                                onChangeTradeStatus={() =>
                                    setTradeStatusConfirm({
                                        accounts: [account],
                                        action: tradeStatusAction,
                                        totalSelectedCount: 1,
                                    })
                                }
                            />
                        </div>
                    );
                },
                enableSorting: false,
                meta: {
                    cellClassName: 'w-14 px-3 py-4',
                },
            },
        ],
        [householdGUID, isTaxLiabilityLoading, renderSelectionCell, selectedAccountIds, updateSelection],
    );
    const groupAccounts = useMemo(
        () =>
            groupBy === 'none'
                ? undefined
                : (account: HouseholdAccountRow) => getAccountGroupKey(account, groupBy, accountGroupProfiles),
        [groupBy, accountGroupProfiles],
    );
    const selectedAccounts = accounts.filter((account) => selectedAccountIds.has(account.accountGUID));
    const tradingStatusMutableAccounts = selectedAccounts.filter(canChangeTradingStatus);
    const suspendableAccounts = tradingStatusMutableAccounts.filter((account) => !account.tradingSuspendedAt);
    const enableableAccounts = tradingStatusMutableAccounts.filter((account) => !!account.tradingSuspendedAt);

    return (
        <div className={`flex flex-col gap-3 ${selectedAccountIds.size > 0 ? 'pb-20' : ''}`}>
            <div className="flex items-center gap-2">
                <span className="text-cocoa-500 text-xs font-medium">Group by</span>
                <Select value={groupBy} onValueChange={(value) => setGroupBy(value as AccountGrouping)}>
                    <SelectTrigger className="w-40 rounded-full">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="accountGroup">Account Group</SelectItem>
                        <SelectItem value="accountType">Account Type</SelectItem>
                        <SelectItem value="boostStatus">Boost Status</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <Card className="border-border overflow-hidden rounded-2xl border bg-white">
                <ClientDataTable
                    data={tableAccounts}
                    columns={columns}
                    isLoading={isLoading}
                    emptyMessage="No accounts available"
                    initialState={{
                        sorting: [{ id: 'aum', desc: true }],
                        columnFilters: [
                            {
                                id: 'tradingStatus',
                                value: [
                                    ...Object.values(tradingStatusPresentations).map(({ label }) => label),
                                    'Held Away',
                                    'Closed',
                                ],
                            },
                        ],
                    }}
                    groupBy={groupAccounts}
                    rowClassName={(account, row) =>
                        !row.getIsGrouped() && selectedAccountIds.has(account.accountGUID)
                            ? 'bg-cocoa-100/50 hover:!bg-cocoa-100/70'
                            : ''
                    }
                />
            </Card>
            {selectedAccountIds.size > 0 && (
                <BulkSelectionBar
                    selectionLabel={`${selectedAccountIds.size} ${selectedAccountIds.size === 1 ? 'account' : 'accounts'}`}
                    onClear={() => setSelectedAccountIds(new Set())}
                >
                    {suspendableAccounts.length > 0 && (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                                setTradeStatusConfirm({
                                    accounts: suspendableAccounts,
                                    action: 'suspend',
                                    totalSelectedCount: selectedAccounts.length,
                                })
                            }
                        >
                            <LuPause /> Suspend trading
                        </Button>
                    )}
                    {enableableAccounts.length > 0 && (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                                setTradeStatusConfirm({
                                    accounts: enableableAccounts,
                                    action: 'enable',
                                    totalSelectedCount: selectedAccounts.length,
                                })
                            }
                        >
                            <LuPlay /> Enable trading
                        </Button>
                    )}
                </BulkSelectionBar>
            )}
            {accountToDelete && (
                <DeleteValidationModal
                    recordName={accountToDelete.accountName.plainText}
                    disabled={isDeletingAccount}
                    onDelete={() => deleteAccount(accountToDelete)}
                    onClose={() => setAccountToDelete(undefined)}
                />
            )}
            {tradeStatusConfirm && (
                <TradeStatusConfirmDialog
                    accounts={tradeStatusConfirm.accounts}
                    action={tradeStatusConfirm.action}
                    totalSelectedCount={tradeStatusConfirm.totalSelectedCount}
                    isPending={isUpdatingTradeStatus}
                    onClose={() => setTradeStatusConfirm(null)}
                    onConfirm={() =>
                        setAccountsTradeStatus(
                            {
                                accountGUIDs: tradeStatusConfirm.accounts.map((account) => account.accountGUID),
                                action: tradeStatusConfirm.action,
                            },
                            { onSuccess: () => setTradeStatusConfirm(null) },
                        )
                    }
                />
            )}
        </div>
    );
};
